import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import { getAphMaxLtvRatio } from "../utils/cmhc";

const COLORS = {
  primary: "#0f5d73",
  primaryDark: "#0a4454",
  accent: "#16a34a",
  danger: "#dc2626",
  warning: "#d97706",
  text: "#0f172a",
  muted: "#64748b",
  line: "#dbe3ea",
  soft: "#f6f9fc",
  card: "#ffffff",
};

const STRINGS = {
  fr: {
    brandSubtitle: "rapport de rentabilité",
    reportDate: "Date du rapport",
    preparedFor: "Préparé pour",
    units: "unités",
    multifamily: "Multilogement",
    financing: "Financement",
    noImage: "Aucune photo d'immeuble dans ce dossier",
    purchasePrice: "Prix d'achat",
    asking: "Demande",
    grossIncome: "Revenus bruts",
    effective: "Effectif",
    noi: "RND",
    noiHint: "Stabilisé / effectif",
    debtService: "Service de la dette",
    monthly: "Mensuel",
    cashFlow: "Cash flow",
    cashFlowHint: "Année 1 (annuel)",
    totalInvestment: "Investissement total",
    invHint: "Mise de fonds + frais",
    capRate: "Taux de capitalisation",
    cashOnCash: "Rendement cash-on-cash",
    ltv: "Ratio prêt-valeur",
    dscr: "Ratio couverture dette",
    grm: "Multiplicateur loyer brut",
    grmKpi: "MRB (GRM)",
    nimKpi: "MRN (NIM)",
    pricePerDoor: "Prix par porte",
    loanPaydown1y: "Rend. capitalisation prêt (1 an)",
    appreciationReturnKpi: "Rendement appréciation",
    valueGeneratedYr1: "Valeur générée (an 1)",
    totalReturn1y: "Rendement global (1 an)",
    financialSummary: "Sommaire financier",
    effectiveIncome: "Revenu effectif",
    operatingExpenses: "Dépenses d'exploitation",
    totalExpenses: "Dépenses totales",
    netOperatingIncome: "Revenu net d'exploitation",
    annualDebtService: "Service annuel de la dette",
    annualCashFlow: "Cash flow annuel",
    financingSummary: "Sommaire du financement",
    interestRate: "Taux d'intérêt",
    qualificationRate: "Taux de qualification",
    amortization: "Amortissement",
    aphPoints: "Points APH",
    maxLoan: "Prêt maximal",
    downPayment: "Mise de fonds",
    acquisitionFees: "Frais d'acquisition",
    generatedBy: "Généré par Rentalyzer",
    disclaimerShort:
      "Document informatif — non constitutif d'un conseil financier, fiscal ou légal.",
    scenarioBrand: "analyse de scénario futur",
    scenarioTitle: "Détail du scénario futur",
    scenarioIntro:
      "Cette page résume le sous-scénario sélectionné et la structure de financement projetée selon les hypothèses saisies dans Rentalyzer.",
    scenarioAssumptions: "Hypothèses du scénario",
    horizon: "Horizon de projection",
    incomeGrowth: "Croissance des revenus",
    expenseGrowth: "Croissance des dépenses",
    valueAppreciation: "Appréciation de la valeur",
    projectedReturns: "Rendements et création de valeur projetés",
    returnHypotheses: "Hypothèses de rendement",
    savedReturnScenario: "Scénario sauvegardé",
    assumptionRevenue: "Revenus",
    assumptionExpense: "Dépenses",
    assumptionValue: "Valeur immeuble",
    advancedScheduleTitle: "Projection année par année",
    pdfYearCol: "Année",
    pdfNetIncome: "Revenu net",
    overallReturn: "Rendement global",
    annualizedReturn: "Rendement annualisé",
    valueGenerated: "Valeur générée",
    equityWithdrawal: "Retrait d'équité",
    projectedFinancial: "Sommaire financier projeté",
    projectedFinancing: "Sommaire financement (scénario)",
    projectedGross: "Revenus bruts projetés",
    projectedEffective: "Revenu effectif projeté",
    projectedOpEx: "Dépenses d'exploitation projetées",
    projectedTotalExp: "Dépenses totales projetées",
    projectedNoi: "RND projeté",
    projectedDebt: "Service de la dette annuel projeté",
    projectedCf: "Cash flow annuel projeté",
    propValue: "Valeur de l'immeuble",
    borrowingValue: "Valeur d'emprunt",
    cmhcPremium: "Prime SCHL",
    totalLoanPremium: "Prêt total incl. prime",
    trappedEquity: "Équité retenue",
    financingFees: "Frais de financement",
    monthlyPayment: "Paiement mensuel",
    scenarioFootnote:
      "Les résultats projetés sont des estimations de modèle et doivent être validés avant toute décision.",
    scenarioAssumptionsText: "Hypothèses",
    scenarioReturnsText: "Rendements indicatifs",
    conventional: "Conventionnel",
    cmhc: "SCHL standard",
    cmhcAph: "SCHL APH Select",
    private: "Prêt privé",
    assumption: "Assomption hypothécaire",
    existing: "Financement en place",
    none: "Aucune hypothèque",
    marketValue: "Valeur estimée",
    outstandingLoan: "Solde du prêt",
    remainingAmortMonths: "Amortissement restant",
    termEnd: "Fin de terme",
    netEquity: "Équité nette",
    propertyLine: "Adresse",
    scenarioLabel: "Scénario",
  },
  en: {
    brandSubtitle: "profitability analysis report",
    reportDate: "Report date",
    preparedFor: "Prepared for",
    units: "units",
    multifamily: "Multifamily",
    financing: "Financing",
    noImage: "No building image in this property file",
    purchasePrice: "Purchase price",
    asking: "Asking",
    grossIncome: "Gross income",
    effective: "Effective",
    noi: "NOI",
    noiHint: "Stabilized / effective",
    debtService: "Debt service",
    monthly: "Monthly",
    cashFlow: "Cash flow",
    cashFlowHint: "Year 1 annual",
    totalInvestment: "Total investment",
    invHint: "Down payment + fees",
    capRate: "Cap rate",
    cashOnCash: "Cash on cash",
    ltv: "Loan to value",
    dscr: "Debt coverage ratio",
    grm: "Gross rent multiplier",
    grmKpi: "GRM",
    nimKpi: "NIM",
    pricePerDoor: "Price per door",
    loanPaydown1y: "Loan paydown return (1yr)",
    appreciationReturnKpi: "Appreciation return",
    valueGeneratedYr1: "Value generated (yr 1)",
    totalReturn1y: "Total return (1 yr)",
    financialSummary: "Financial summary",
    effectiveIncome: "Effective income",
    operatingExpenses: "Operating expenses",
    totalExpenses: "Total expenses",
    netOperatingIncome: "Net operating income",
    annualDebtService: "Annual debt service",
    annualCashFlow: "Annual cash flow",
    financingSummary: "Financing summary",
    interestRate: "Interest rate",
    qualificationRate: "Qualification rate",
    amortization: "Amortization",
    aphPoints: "APH points",
    maxLoan: "Maximum loan",
    downPayment: "Down payment",
    acquisitionFees: "Acquisition fees",
    generatedBy: "Generated by Rentalyzer",
    disclaimerShort:
      "Informational only — not financial, tax, or legal advice.",
    scenarioBrand: "future scenario analysis",
    scenarioTitle: "Future scenario details",
    scenarioIntro:
      "This page summarizes the selected sub-scenario and projected financing based on assumptions entered in Rentalyzer.",
    scenarioAssumptions: "Scenario assumptions",
    horizon: "Projection horizon",
    incomeGrowth: "Income growth",
    expenseGrowth: "Expense growth",
    valueAppreciation: "Value appreciation",
    projectedReturns: "Projected returns and value creation",
    returnHypotheses: "Return assumptions",
    savedReturnScenario: "Saved scenario",
    assumptionRevenue: "Revenue",
    assumptionExpense: "Expenses",
    assumptionValue: "Property value",
    advancedScheduleTitle: "Year-by-year projection",
    pdfYearCol: "Year",
    pdfNetIncome: "Net income",
    overallReturn: "Overall return",
    annualizedReturn: "Annualized return",
    valueGenerated: "Value generated",
    equityWithdrawal: "Equity withdrawal",
    projectedFinancial: "Projected financial summary",
    projectedFinancing: "Scenario financing summary",
    projectedGross: "Projected gross income",
    projectedEffective: "Projected effective income",
    projectedOpEx: "Projected operating expenses",
    projectedTotalExp: "Projected total expenses",
    projectedNoi: "Projected NOI",
    projectedDebt: "Projected annual debt service",
    projectedCf: "Projected cash flow",
    propValue: "Property value",
    borrowingValue: "Borrowing value",
    cmhcPremium: "CMHC premium",
    totalLoanPremium: "Total loan incl. premium",
    trappedEquity: "Trapped equity",
    financingFees: "Financing fees",
    monthlyPayment: "Monthly payment",
    scenarioFootnote:
      "Projected results are model estimates and should be validated before any decision.",
    scenarioAssumptionsText: "Assumptions",
    scenarioReturnsText: "Indicative returns",
    conventional: "Conventional",
    cmhc: "CMHC standard",
    cmhcAph: "CMHC APH Select",
    private: "Private loan",
    assumption: "Mortgage assumption",
    existing: "In-place financing",
    none: "No mortgage",
    marketValue: "Estimated market value",
    outstandingLoan: "Outstanding loan",
    remainingAmortMonths: "Remaining amortization",
    termEnd: "Term end",
    netEquity: "Net equity",
    propertyLine: "Property",
    scenarioLabel: "Scenario",
  },
};

const styles = StyleSheet.create({
  page: {
    padding: 36,
    paddingBottom: 48,
    fontFamily: "Helvetica",
    fontSize: 9,
    color: COLORS.text,
    position: "relative",
  },
  topbar: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    borderBottomWidth: 2,
    borderBottomColor: COLORS.line,
    paddingBottom: 10,
    marginBottom: 14,
  },
  brandRow: { flexDirection: "row", alignItems: "center", gap: 10 },
  brandTitle: {
    fontSize: 18,
    fontWeight: "bold",
    color: COLORS.primaryDark,
    fontFamily: "Helvetica",
  },
  brandSub: {
    fontSize: 9,
    color: COLORS.muted,
    marginTop: 2,
    fontFamily: "Helvetica",
  },
  meta: {
    textAlign: "right",
    fontSize: 9,
    color: COLORS.muted,
    lineHeight: 1.5,
    fontFamily: "Helvetica",
  },
  metaStrong: { fontWeight: "bold", color: COLORS.text },
  propertyHeader: {
    flexDirection: "row",
    gap: 14,
    marginBottom: 12,
  },
  propertyTitleCol: { flex: 1.3 },
  heroCol: { flex: 0.7, minHeight: 120 },
  propertyAddress: {
    fontSize: 13,
    fontWeight: "bold",
    marginBottom: 5,
    color: COLORS.text,
    fontFamily: "Helvetica",
    lineHeight: 1.25,
  },
  subMuted: {
    fontSize: 9,
    color: COLORS.muted,
    lineHeight: 1.45,
    fontFamily: "Helvetica",
  },
  heroBox: {
    borderWidth: 1,
    borderColor: COLORS.line,
    borderRadius: 10,
    backgroundColor: COLORS.soft,
    overflow: "hidden",
    minHeight: 120,
    justifyContent: "center",
    alignItems: "center",
  },
  heroImg: { width: "100%", height: 120, objectFit: "cover" },
  heroPlaceholder: {
    padding: 12,
    textAlign: "center",
    fontSize: 9,
    color: COLORS.muted,
  },
  /** Rangée 3 colonnes : largeurs égales, cartes alignées (évite flexWrap + % bancals). */
  cardRow: {
    flexDirection: "row",
    alignItems: "stretch",
    marginBottom: 5,
  },
  cardCol: {
    flex: 1,
    flexBasis: 0,
    paddingHorizontal: 4,
  },
  labelCaps: {
    fontSize: 7,
    color: COLORS.muted,
    textTransform: "uppercase",
    letterSpacing: 0.35,
    lineHeight: 1.2,
    marginBottom: 2,
    fontFamily: "Helvetica",
  },
  valueLg: {
    fontSize: 14,
    fontWeight: "bold",
    color: COLORS.text,
    lineHeight: 1.15,
    textAlign: "right",
    fontFamily: "Helvetica",
  },
  hintSm: {
    fontSize: 7.5,
    color: COLORS.muted,
    lineHeight: 1.3,
    textAlign: "right",
    fontFamily: "Helvetica",
  },
  /** Tuiles métriques compactes (grille 5×3) — Helvetica partout */
  metricTile: {
    width: "100%",
    borderWidth: 1,
    borderColor: COLORS.line,
    borderRadius: 8,
    paddingVertical: 5,
    paddingHorizontal: 7,
    backgroundColor: COLORS.soft,
    fontFamily: "Helvetica",
  },
  metricTileLabel: {
    fontSize: 6.5,
    color: COLORS.muted,
    textTransform: "uppercase",
    letterSpacing: 0.35,
    lineHeight: 1.2,
    marginBottom: 2,
    fontFamily: "Helvetica",
  },
  metricTileValue: {
    fontSize: 11,
    fontWeight: "bold",
    textAlign: "right",
    lineHeight: 1.1,
    fontFamily: "Helvetica",
  },
  mainGrid: {
    flexDirection: "row",
    alignItems: "stretch",
    gap: 12,
    marginTop: 4,
  },
  panel: {
    flex: 1,
    flexBasis: 0,
    borderWidth: 1,
    borderColor: COLORS.line,
    borderRadius: 12,
    padding: 12,
  },
  panelTitle: {
    fontSize: 11,
    fontWeight: "bold",
    color: COLORS.primaryDark,
    marginBottom: 8,
    fontFamily: "Helvetica",
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 5,
    borderBottomWidth: 1,
    borderBottomColor: "#edf2f7",
  },
  rowLast: { borderBottomWidth: 0 },
  cellLabel: {
    width: "58%",
    fontSize: 9,
    color: COLORS.muted,
    fontFamily: "Helvetica",
  },
  cellValue: {
    width: "42%",
    fontSize: 9,
    fontWeight: "bold",
    textAlign: "right",
    color: COLORS.text,
    fontFamily: "Helvetica",
  },
  footer: {
    position: "absolute",
    bottom: 28,
    left: 36,
    right: 36,
    flexDirection: "row",
    justifyContent: "space-between",
    borderTopWidth: 1,
    borderTopColor: COLORS.line,
    paddingTop: 6,
    fontSize: 8,
    color: COLORS.muted,
    fontFamily: "Helvetica",
  },
  pos: { color: COLORS.accent },
  neg: { color: COLORS.danger },
  neu: { color: COLORS.primaryDark },
  scenarioBanner: {
    backgroundColor: COLORS.primary,
    borderRadius: 12,
    padding: 14,
    marginBottom: 12,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  scenarioBannerTitle: {
    color: "#fff",
    fontSize: 14,
    fontWeight: "bold",
    marginBottom: 4,
  },
  scenarioBannerDesc: { color: "#fff", fontSize: 9, opacity: 0.95 },
  scenarioTag: {
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.35)",
    borderRadius: 20,
    paddingVertical: 6,
    paddingHorizontal: 10,
    color: "#fff",
    fontSize: 8,
    fontWeight: "bold",
  },
  scenarioPlainBlock: {
    marginBottom: 10,
    padding: 10,
    borderWidth: 1,
    borderColor: COLORS.line,
    borderRadius: 10,
    backgroundColor: COLORS.soft,
  },
  scenarioPlainTitle: {
    fontSize: 10,
    fontWeight: "bold",
    color: COLORS.primaryDark,
    marginBottom: 5,
    fontFamily: "Helvetica",
  },
  scenarioPlainBody: {
    fontSize: 9,
    color: COLORS.text,
    lineHeight: 1.45,
    fontFamily: "Helvetica",
  },
  footnote: {
    marginTop: 8,
    fontSize: 8,
    color: COLORS.muted,
    lineHeight: 1.4,
  },
  h3scenario: {
    fontSize: 14,
    fontWeight: "bold",
    color: COLORS.primaryDark,
    marginBottom: 8,
    fontFamily: "Helvetica",
  },
  intro: {
    fontSize: 9,
    color: COLORS.muted,
    marginBottom: 12,
    lineHeight: 1.45,
    fontFamily: "Helvetica",
  },
});

const formatCurrency = (value, locale = "fr-CA") =>
  new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "CAD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);

const formatPercent = (value, digits = 1, locale = "fr-CA") =>
  `${new Intl.NumberFormat(locale, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value || 0)}%`;

function financingLabel(ft, L) {
  if (ft === "conventional") return L.conventional;
  if (ft === "cmhc") return L.cmhc;
  if (ft === "cmhc_aph") return L.cmhcAph;
  if (ft === "private") return L.private;
  if (ft === "assumption") return L.assumption;
  if (ft === "existing") return L.existing;
  if (ft === "none") return L.none;
  return ft ? String(ft) : "—";
}

function computeSchlCompliance(analysis, property) {
  const operatingSchl = analysis?.operatingExpensesSCHL || 0;
  const operatingTotal = analysis?.operatingExpensesTotal || 0;
  const loanValueRatio = analysis?.loanValueRatio || 0;
  const hasSchl = operatingSchl > 0 && operatingTotal > 0;
  const ratio = hasSchl ? operatingTotal / operatingSchl : null;
  const diffPct = ratio != null ? (ratio - 1) * 100 : null;
  let expensesStatus = null;
  if (diffPct != null) {
    if (diffPct < -10) expensesStatus = "below";
    else if (diffPct > 10) expensesStatus = "above";
    else expensesStatus = "within";
  }
  const financingType = property?.financingType;
  const points = parseInt(property?.aphPoints || 0, 10) || 0;
  let ltvMax = null;
  let ltvLabel = null;
  if (financingType === "cmhc_aph") {
    ltvMax = getAphMaxLtvRatio(points) * 100;
    ltvLabel = "RPV max APH (approx.)";
  } else if (financingType === "cmhc") {
    ltvMax = 85;
    ltvLabel = "RPV max SCHL (approx.)";
  }
  let ltvStatus = null;
  if (ltvMax != null) {
    ltvStatus = loanValueRatio <= ltvMax ? "ok" : "tooHigh";
  }
  return {
    hasSchl,
    diffPct,
    expensesStatus,
    loanValueRatio,
    ltvMax,
    ltvLabel,
    ltvStatus,
  };
}

function DataRow({ label, value, last, valueStyle }) {
  return (
    <View style={[styles.row, last && styles.rowLast]}>
      <Text style={styles.cellLabel}>{label}</Text>
      <Text style={[styles.cellValue, valueStyle]}>{value}</Text>
    </View>
  );
}

function MetricTile({ label, value, valueStyle }) {
  return (
    <View style={styles.metricTile}>
      <Text style={styles.metricTileLabel}>{label}</Text>
      <Text style={[styles.metricTileValue, valueStyle]}>{value}</Text>
    </View>
  );
}

/**
 * @param {object} props
 * @param {string} [props.title] - PDF document title metadata
 * @param {object} props.property
 * @param {object} props.analysis
 * @param {{ years: number, totalReturn: number, annualizedReturn: number, irr: number, assumptionSummary?: {key:string,text:string}[], loadedProjectionTitle?: string, outlookLabel?: string }} [props.futureReturns]
 * @param {{ label: string, createdAt: any }} [props.marketParamsVersion]
 * @param {string} [props.preparedFor] - Recipient name (report header)
 * @param {string} [props.buildingImageUrl] - Optional hero image URL
 * @param {string} [props.logoSrc] - Absolute URL to logo for PDF
 * @param {'fr'|'en'} [props.language]
 * @param {boolean} [props.showScenarioPage]
 * @param {object|null} [props.scenarioPdf] - Bundle for optional scenario page
 */
export function RentalizerPdfDocument({
  title = "Rentalyzer",
  property,
  analysis,
  futureReturns,
  marketParamsVersion = null,
  preparedFor = "",
  buildingImageUrl = "",
  logoSrc = "",
  language = "fr",
  showScenarioPage = false,
  scenarioPdf = null,
}) {
  const L = STRINGS[language] || STRINGS.fr;
  const locale = language === "en" ? "en-CA" : "fr-CA";
  const fullAddress = [
    property?.address,
    property?.city,
    property?.province,
    property?.postalCode,
  ]
    .filter(Boolean)
    .join(", ");
  /** Ligne principale : rue seulement (ville / province / CP en dessous). */
  const streetLine =
    String(property?.address || "").trim() ||
    (fullAddress ? fullAddress.split(",")[0].trim() : "") ||
    "—";
  const cityLine = [property?.city, property?.province, property?.postalCode]
    .filter(Boolean)
    .join(", ");
  const isCashflowPositive = (analysis?.cashFlow || 0) >= 0;
  const cocPos = (analysis?.cashOnCashReturn || 0) >= 0;
  const schl = computeSchlCompliance(analysis, property || {});
  const formattedDate = new Date().toLocaleDateString(locale);
  const recipient =
    (preparedFor && String(preparedFor).trim()) ||
    property?.clientName ||
    "—";
  const units = property?.numberOfUnits || 0;
  const assetType = L.multifamily;
  const finLabel = financingLabel(property?.financingType, L);
  const isOwned = property?.dealPurpose === "owned" || analysis?.isOwnedDeal;
  const isInPlace =
    property?.financingType === "assumption" ||
    property?.financingType === "existing";
  const isNone = property?.financingType === "none";
  const grmVal = analysis?.grossRentMultiplier
    ? analysis.grossRentMultiplier.toFixed(2)
    : "—";
  const nimVal =
    analysis?.netIncomeMultiplier != null && analysis.netIncomeMultiplier > 0
      ? analysis.netIncomeMultiplier.toFixed(1)
      : "—";

  const sa = scenarioPdf?.analysis;
  const sp = scenarioPdf?.property;
  const scenarioCfPos = sa ? (sa.cashFlow || 0) >= 0 : false;
  const scenarioLtvHigh =
    sa && (sa.loanValueRatio || 0) > 85 ? styles.neg : styles.neu;

  return (
    <Document
      title={title}
      author="Rentalyzer"
      subject={L.brandSubtitle}
      creator="Rentalyzer"
    >
      <Page size="A4" style={styles.page}>
        <View style={styles.topbar} fixed>
          <View style={styles.brandRow}>
            {logoSrc ? (
              <Image src={logoSrc} style={{ width: 36, height: 36 }} />
            ) : (
              <View style={{ width: 36, height: 36 }} />
            )}
            <View>
              <Text style={styles.brandTitle}>Rentalyzer</Text>
              <Text style={styles.brandSub}>{L.brandSubtitle}</Text>
            </View>
          </View>
          <View style={styles.meta}>
            <Text>
              <Text style={styles.metaStrong}>{L.reportDate}: </Text>
              {formattedDate}
            </Text>
            <Text>
              <Text style={styles.metaStrong}>{L.preparedFor}: </Text>
              {recipient}
            </Text>
          </View>
        </View>

        <View style={styles.propertyHeader}>
          <View style={styles.propertyTitleCol}>
            <Text style={styles.propertyAddress}>{streetLine}</Text>
            <Text style={styles.subMuted}>
              {cityLine ? `${cityLine}\n` : ""}
              {units} {L.units} · {assetType}
              {"\n"}
              {L.financing}: {finLabel}
            </Text>
          </View>
          <View style={styles.heroCol}>
            <View style={styles.heroBox}>
              {buildingImageUrl ? (
                <Image src={buildingImageUrl} style={styles.heroImg} />
              ) : (
                <Text style={styles.heroPlaceholder}>{L.noImage}</Text>
              )}
            </View>
          </View>
        </View>

        <View style={styles.cardRow}>
          <View style={styles.cardCol}>
            <MetricTile
              label={isOwned ? L.marketValue : L.purchasePrice}
              value={formatCurrency(Number(property?.purchasePrice) || 0, locale)}
            />
          </View>
          <View style={styles.cardCol}>
            <MetricTile
              label={L.pricePerDoor}
              value={formatCurrency(
                Math.round(analysis?.pricePerUnit || 0),
                locale,
              )}
            />
          </View>
          <View style={styles.cardCol}>
            <MetricTile
              label={isOwned ? L.netEquity : L.totalInvestment}
              value={formatCurrency(analysis?.totalInvestment, locale)}
            />
          </View>
        </View>
        <View style={styles.cardRow}>
          <View style={styles.cardCol}>
            <MetricTile
              label={L.grossIncome}
              value={formatCurrency(analysis?.totalGrossRevenue, locale)}
            />
          </View>
          <View style={styles.cardCol}>
            <MetricTile
              label={L.debtService}
              value={formatCurrency(analysis?.annualDebtService, locale)}
            />
          </View>
          <View style={styles.cardCol}>
            <MetricTile
              label={L.noi}
              value={formatCurrency(analysis?.netOperatingIncome, locale)}
            />
          </View>
        </View>
        <View style={styles.cardRow}>
          <View style={styles.cardCol}>
            <MetricTile label={L.grmKpi} value={String(grmVal)} />
          </View>
          <View style={styles.cardCol}>
            <MetricTile label={L.nimKpi} value={nimVal} />
          </View>
          <View style={styles.cardCol}>
            <MetricTile
              label={L.capRate}
              value={formatPercent(analysis?.capRate, 2, locale)}
            />
          </View>
        </View>
        <View style={styles.cardRow}>
          <View style={styles.cardCol}>
            <MetricTile
              label={L.cashOnCash}
              value={formatPercent(analysis?.cashOnCashReturn, 2, locale)}
              valueStyle={cocPos ? styles.pos : styles.neg}
            />
          </View>
          <View style={styles.cardCol}>
            <MetricTile
              label={L.loanPaydown1y}
              value={formatPercent(analysis?.loanPaydownReturn, 2, locale)}
            />
          </View>
          <View style={styles.cardCol}>
            <MetricTile
              label={L.appreciationReturnKpi}
              value={formatPercent(analysis?.appreciationReturn, 2, locale)}
            />
          </View>
        </View>
        <View style={styles.cardRow}>
          <View style={styles.cardCol}>
            <MetricTile
              label={L.valueGeneratedYr1}
              value={formatCurrency(analysis?.valueGeneratedYear1, locale)}
              valueStyle={styles.pos}
            />
          </View>
          <View style={styles.cardCol}>
            <MetricTile
              label={L.totalReturn1y}
              value={formatPercent(analysis?.totalReturn, 2, locale)}
              valueStyle={styles.pos}
            />
          </View>
          <View style={styles.cardCol}>
            <MetricTile
              label={L.ltv}
              value={formatPercent(analysis?.loanValueRatio, 2, locale)}
            />
          </View>
        </View>

        <View style={styles.mainGrid}>
          <View style={styles.panel}>
            <Text style={styles.panelTitle}>{L.financialSummary}</Text>
            <DataRow
              label={L.grossIncome}
              value={formatCurrency(analysis?.totalGrossRevenue, locale)}
            />
            <DataRow
              label={L.effectiveIncome}
              value={formatCurrency(analysis?.effectiveNetIncome, locale)}
            />
            <DataRow
              label={L.operatingExpenses}
              value={formatCurrency(analysis?.operatingExpensesTotal, locale)}
            />
            <DataRow
              label={L.totalExpenses}
              value={formatCurrency(analysis?.totalExpenses, locale)}
            />
            <DataRow
              label={L.netOperatingIncome}
              value={formatCurrency(analysis?.netOperatingIncome, locale)}
            />
            <DataRow
              label={L.annualDebtService}
              value={formatCurrency(analysis?.annualDebtService, locale)}
            />
            <DataRow
              label={L.annualCashFlow}
              value={formatCurrency(analysis?.cashFlow, locale)}
              last
              valueStyle={isCashflowPositive ? styles.pos : styles.neg}
            />
          </View>
          <View style={styles.panel}>
            <Text style={styles.panelTitle}>{L.financingSummary}</Text>
            <DataRow label={L.financing} value={finLabel} />
            {!isNone ? (
              <DataRow
                label={L.interestRate}
                value={formatPercent(analysis?.mortgageRate, 2, locale)}
              />
            ) : null}
            {!isInPlace && !isNone ? (
              <DataRow
                label={L.qualificationRate}
                value={formatPercent(
                  parseFloat(property?.qualificationRate) || 0,
                  2,
                  locale,
                )}
              />
            ) : null}
            {isInPlace && analysis?.assumptionDerivedRemainingMonths != null ? (
              <DataRow
                label={L.remainingAmortMonths || L.amortization}
                value={`${new Intl.NumberFormat(locale, {
                  maximumFractionDigits: 1,
                }).format(analysis.assumptionDerivedRemainingMonths)} ${
                  language === "fr" ? "mois" : "mo"
                }`}
              />
            ) : !isInPlace && !isNone ? (
              <DataRow
                label={L.amortization}
                value={`${property?.amortization || "—"} ${language === "fr" ? "ans" : "yr"}`}
              />
            ) : null}
            {isInPlace && property?.assumptionTermEnd ? (
              <DataRow label={L.termEnd || L.amortization} value={String(property.assumptionTermEnd)} />
            ) : null}
            {property?.financingType === "cmhc_aph" ? (
              <DataRow label={L.aphPoints} value={`${property?.aphPoints ?? 0}`} />
            ) : null}
            {!isNone ? (
              <DataRow
                label={isInPlace ? (L.outstandingLoan || L.maxLoan) : L.maxLoan}
                value={formatCurrency(analysis?.maxLoanAmount, locale)}
              />
            ) : null}
            <DataRow
              label={isOwned ? (L.netEquity || L.downPayment) : L.downPayment}
              value={formatCurrency(
                isOwned
                  ? analysis?.equity ?? analysis?.downPayment
                  : analysis?.downPayment,
                locale,
              )}
            />
            {!isOwned ? (
              <DataRow
                label={L.acquisitionFees}
                value={formatCurrency(analysis?.acquisitionCosts, locale)}
              />
            ) : null}
            <DataRow
              label={isOwned ? (L.netEquity || L.totalInvestment) : L.totalInvestment}
              value={formatCurrency(analysis?.totalInvestment, locale)}
              last
            />
          </View>
        </View>

        {futureReturns ? (
          <View style={[styles.panel, { marginTop: 10 }]}>
            <Text style={styles.panelTitle}>
              {language === "fr"
                ? `Rendements projetés sur ${futureReturns.years} an(s)`
                : `Projected returns over ${futureReturns.years} year(s)`}
            </Text>
            {futureReturns.loadedProjectionTitle ? (
              <Text style={[styles.hintSm, { marginBottom: 6 }]}>
                {L.savedReturnScenario}: {futureReturns.loadedProjectionTitle}
                {futureReturns.outlookLabel
                  ? ` (${futureReturns.outlookLabel})`
                  : ""}
              </Text>
            ) : null}
            {futureReturns.assumptionSummary?.length ? (
              <View style={{ marginBottom: 8 }}>
                <Text style={[styles.hintSm, { marginBottom: 4 }]}>
                  {L.returnHypotheses}
                </Text>
                {futureReturns.assumptionSummary.map((line) => {
                  const labelKey =
                    line.key === "revenue"
                      ? L.assumptionRevenue
                      : line.key === "expense"
                        ? L.assumptionExpense
                        : L.assumptionValue;
                  return (
                    <Text key={line.key} style={styles.scenarioPlainBody}>
                      {labelKey}: {line.text}
                    </Text>
                  );
                })}
              </View>
            ) : null}
            {futureReturns.advancedScheduleRows?.length ? (
              <View style={{ marginBottom: 8 }}>
                <Text style={[styles.hintSm, { marginBottom: 4 }]}>
                  {L.advancedScheduleTitle}
                </Text>
                <Text style={[styles.hintSm, { marginBottom: 2 }]}>
                  {L.pdfYearCol} | {L.assumptionRevenue} | {L.assumptionExpense} | {L.assumptionValue} | {L.pdfNetIncome}
                </Text>
                {futureReturns.advancedScheduleRows.map((row) => (
                  <Text key={row.year} style={styles.scenarioPlainBody}>
                    {L.pdfYearCol} {row.year}: {row.revenue} · {row.expense} · {row.value} · {row.netIncome}
                  </Text>
                ))}
              </View>
            ) : null}
            <View style={{ flexDirection: "row", gap: 12 }}>
              <View style={{ flex: 1 }}>
                <Text style={styles.hintSm}>{L.overallReturn}</Text>
                <Text style={[styles.valueLg, styles.pos]}>
                  {formatPercent(futureReturns.totalReturn, 2, locale)}
                </Text>
              </View>
              <View style={{ flex: 1 }}>
                <Text style={styles.hintSm}>{L.annualizedReturn}</Text>
                <Text style={[styles.valueLg, styles.pos]}>
                  {formatPercent(futureReturns.annualizedReturn, 2, locale)}
                </Text>
              </View>
              <View style={{ flex: 1 }}>
                <Text style={styles.hintSm}>TRI / IRR</Text>
                <Text style={[styles.valueLg, styles.pos]}>
                  {formatPercent(futureReturns.irr, 2, locale)}
                </Text>
              </View>
            </View>
          </View>
        ) : null}

        {marketParamsVersion?.label ? (
          <Text style={[styles.footnote, { marginTop: 6 }]}>
            {language === "fr" ? "Hypothèses marché" : "Market assumptions"}{" "}
            {marketParamsVersion.label}
            {marketParamsVersion.createdAt
              ? ` — ${new Date(marketParamsVersion.createdAt).toLocaleDateString(locale)}`
              : ""}
          </Text>
        ) : null}

        {schl.hasSchl && schl.diffPct != null ? (
          <Text style={styles.footnote}>
            SCHL:{" "}
            {language === "fr"
              ? `écart dépenses / barème ≈ ${formatPercent(schl.diffPct, 1, locale)}`
              : `expense vs benchmark ≈ ${formatPercent(schl.diffPct, 1, locale)}`}
            {schl.ltvStatus === "tooHigh"
              ? language === "fr"
                ? " — RPV élevé vs barème typique."
                : " — LTV above typical benchmark."
              : ""}
          </Text>
        ) : null}

        <View style={styles.footer} fixed>
          <Text>{L.generatedBy}</Text>
          <Text style={{ maxWidth: "55%" }}>{L.disclaimerShort}</Text>
        </View>
      </Page>

      {showScenarioPage && scenarioPdf && sa && sp ? (
        <Page size="A4" style={styles.page}>
          <View style={styles.topbar}>
            <View style={styles.brandRow}>
              {logoSrc ? (
                <Image src={logoSrc} style={{ width: 36, height: 36 }} />
              ) : null}
              <View>
                <Text style={styles.brandTitle}>Rentalyzer</Text>
                <Text style={styles.brandSub}>{L.scenarioBrand}</Text>
              </View>
            </View>
            <View style={styles.meta}>
              <Text>
                <Text style={styles.metaStrong}>{L.propertyLine}: </Text>
                {fullAddress || "—"}
              </Text>
              <Text>
                <Text style={styles.metaStrong}>{L.scenarioLabel}: </Text>
                {scenarioPdf.scenarioName || "—"}
              </Text>
            </View>
          </View>

          <Text style={styles.h3scenario}>{L.scenarioTitle}</Text>
          <Text style={styles.intro}>{L.scenarioIntro}</Text>

          <View style={styles.scenarioBanner}>
            <View style={{ flex: 1, paddingRight: 8 }}>
              <Text style={styles.scenarioBannerTitle}>
                {scenarioPdf.scenarioName || "—"}
              </Text>
              <Text style={styles.scenarioBannerDesc}>
                {scenarioPdf.scenarioDescription || "—"}
              </Text>
            </View>
            <Text style={styles.scenarioTag}>{scenarioPdf.typeLabel || "—"}</Text>
          </View>

          <View style={styles.scenarioPlainBlock}>
            <Text style={styles.scenarioPlainTitle}>
              {L.scenarioAssumptionsText}
            </Text>
            <Text style={styles.scenarioPlainBody}>
              {L.horizon}: {scenarioPdf.projectionYears ?? "—"}{" "}
              {language === "fr" ? "ans" : "yr"}
              {" · "}
              {L.incomeGrowth}: {scenarioPdf.incomeGrowth || "—"}
              {" · "}
              {L.expenseGrowth}: {scenarioPdf.expenseGrowth || "—"}
              {" · "}
              {L.valueAppreciation}: {scenarioPdf.valueAppreciation || "—"}
            </Text>
          </View>

          <View style={styles.scenarioPlainBlock}>
            <Text style={styles.scenarioPlainTitle}>{L.scenarioReturnsText}</Text>
            <Text style={styles.scenarioPlainBody}>
              {L.overallReturn}: {formatPercent(sa.totalReturn, 2, locale)}
              {" · "}
              {L.cashOnCash}: {formatPercent(sa.cashOnCashReturn, 2, locale)}
              {" · "}
              {L.valueGenerated}: {formatCurrency(sa.valueGeneratedYear1, locale)}
              {scenarioPdf.equityWithdrawal != null
                ? ` · ${L.equityWithdrawal}: ${formatCurrency(
                    scenarioPdf.equityWithdrawal,
                    locale,
                  )}`
                : ""}
            </Text>
          </View>

          <View style={[styles.mainGrid, { marginTop: 8 }]}>
            <View style={styles.panel}>
              <Text style={styles.panelTitle}>{L.projectedFinancial}</Text>
              <DataRow
                label={L.projectedGross}
                value={formatCurrency(sa.totalGrossRevenue, locale)}
              />
              <DataRow
                label={L.projectedEffective}
                value={formatCurrency(sa.effectiveNetIncome, locale)}
              />
              <DataRow
                label={L.projectedOpEx}
                value={formatCurrency(sa.operatingExpensesTotal, locale)}
              />
              <DataRow
                label={L.projectedTotalExp}
                value={formatCurrency(sa.totalExpenses, locale)}
              />
              <DataRow
                label={L.projectedNoi}
                value={formatCurrency(sa.netOperatingIncome, locale)}
              />
              <DataRow
                label={L.projectedDebt}
                value={formatCurrency(sa.annualDebtService, locale)}
              />
              <DataRow
                label={L.projectedCf}
                value={formatCurrency(sa.cashFlow, locale)}
                last
                valueStyle={scenarioCfPos ? styles.pos : styles.neg}
              />
            </View>
            <View style={styles.panel}>
              <Text style={styles.panelTitle}>{L.projectedFinancing}</Text>
              <DataRow
                label={L.financing}
                value={financingLabel(sp.financingType, L)}
              />
              <DataRow
                label={L.interestRate}
                value={formatPercent(sa.mortgageRate, 2, locale)}
              />
              <DataRow
                label={L.amortization}
                value={`${sp.amortization || "—"} ${language === "fr" ? "ans" : "yr"}`}
              />
              {sp?.financingType !== "assumption" &&
              sp?.financingType !== "existing" &&
              sp?.financingType !== "none" ? (
                <DataRow
                  label={L.dscr}
                  value={`${(sa.actualDebtCoverageRatio || 0).toFixed(2)}`}
                />
              ) : null}
              <DataRow
                label={L.propValue}
                value={formatCurrency(
                  sa.economicValue || Number(sp.purchasePrice) || 0,
                  locale,
                )}
              />
              <DataRow
                label={L.borrowingValue}
                value={formatCurrency(sa.economicValue, locale)}
              />
              <DataRow
                label={L.maxLoan}
                value={formatCurrency(sa.maxLoanAmount, locale)}
              />
              <DataRow
                label={L.cmhcPremium}
                value={formatCurrency(sa.cmhcPremium, locale)}
              />
              <DataRow
                label={L.totalLoanPremium}
                value={formatCurrency(sa.totalLoanAmount, locale)}
              />
              <DataRow
                label={L.trappedEquity}
                value={formatCurrency(
                  (sa.economicValue || 0) - (sa.totalLoanAmount || 0),
                  locale,
                )}
              />
              <DataRow
                label={L.financingFees}
                value={formatCurrency(sa.acquisitionCosts, locale)}
              />
              <DataRow
                label={L.monthlyPayment}
                value={formatCurrency(sa.monthlyPayment, locale)}
              />
              <DataRow
                label={L.ltv}
                value={formatPercent(sa.loanValueRatio, 2, locale)}
                last
                valueStyle={scenarioLtvHigh}
              />
            </View>
          </View>

          <Text style={styles.footnote}>{L.scenarioFootnote}</Text>

          <View style={styles.footer} fixed>
            <Text>{L.generatedBy}</Text>
            <Text style={{ maxWidth: "55%" }}>{L.disclaimerShort}</Text>
          </View>
        </Page>
      ) : null}
    </Document>
  );
}
